import { useEffect, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { isAxiosError } from 'axios';
import { apiClient } from '@/lib/apiClient';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { SuggestButton } from '@/components/SuggestButton';

export interface CreatedAccount {
  id: string;
  name: string;
  industry?: string | null;
  sizeBucket?: string | null;
}

interface Props {
  open: boolean;
  initialName?: string;
  onClose: () => void;
  onCreated: (account: CreatedAccount) => void;
}

const SIZE_BUCKETS = ['SMALL', 'MEDIUM', 'LARGE', 'ENTERPRISE'] as const;

/**
 * Layered create-account modal. Sits above whatever modal opened the
 * AccountCombobox so the user does not lose their original form's state.
 * Submits POST /accounts and hands the new id back to the caller.
 */
export function InlineCreateAccountModal({
  open,
  initialName,
  onClose,
  onCreated,
}: Props) {
  const { t } = useTranslation();
  const [name, setName] = useState('');
  const [industry, setIndustry] = useState('');
  const [sizeBucket, setSizeBucket] = useState('');
  const [website, setWebsite] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setName(initialName ?? '');
      setIndustry('');
      setSizeBucket('');
      setWebsite('');
      setError(null);
    }
  }, [open, initialName]);

  if (!open) return null;

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      const body: Record<string, unknown> = { name: name.trim() };
      if (industry.trim()) body.industry = industry.trim();
      if (sizeBucket) body.sizeBucket = sizeBucket;
      if (website.trim()) body.website = website.trim();
      const { data } = await apiClient.post<CreatedAccount>('/accounts', body);
      onCreated(data);
    } catch (err) {
      const detail = isAxiosError(err)
        ? (err.response?.data as { detail?: string } | undefined)?.detail
        : null;
      setError(detail ?? t('inlineCreateAccount.error_generic'));
    } finally {
      setSubmitting(false);
    }
  }

  const canSubmit = !submitting && name.trim().length > 0;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 p-4">
      <div className="w-full max-w-md rounded-lg border bg-card p-6 shadow-xl">
        <h3 className="mb-1 text-lg font-semibold">
          {t('inlineCreateAccount.title')}
        </h3>
        <p className="mb-4 text-sm text-muted-foreground">
          {t('inlineCreateAccount.subtitle')}
        </p>
        <form onSubmit={handleSubmit} className="space-y-3">
          <div className="space-y-1">
            <label className="text-sm font-medium">
              {t('inlineCreateAccount.name')}
              <span className="text-destructive"> *</span>
            </label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              autoFocus
              required
            />
          </div>
          <div className="space-y-1">
            <label className="text-sm font-medium">
              {t('inlineCreateAccount.industry')}
            </label>
            <div className="flex items-start gap-2">
              <Input
                value={industry}
                onChange={(e) => setIndustry(e.target.value)}
                placeholder={t('inlineCreateAccount.industry_placeholder')}
              />
              <SuggestButton
                entity="ACCOUNT"
                field="industry"
                context={{ name }}
                onAccept={setIndustry}
                emphasize={!industry && Boolean(name.trim())}
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <label className="text-sm font-medium">
                {t('inlineCreateAccount.size')}
              </label>
              <select
                value={sizeBucket}
                onChange={(e) => setSizeBucket(e.target.value)}
                className="h-10 w-full rounded-md border bg-transparent px-3 text-sm"
              >
                <option value="">{t('inlineCreateAccount.size_none')}</option>
                {SIZE_BUCKETS.map((s) => (
                  <option key={s} value={s}>
                    {t(`inlineCreateAccount.size_${s.toLowerCase()}`)}
                  </option>
                ))}
              </select>
            </div>
            <div className="space-y-1">
              <label className="text-sm font-medium">
                {t('inlineCreateAccount.website')}
              </label>
              <div className="flex items-start gap-2">
                <Input
                  value={website}
                  onChange={(e) => setWebsite(e.target.value)}
                  placeholder="example.com"
                />
                <SuggestButton
                  entity="ACCOUNT"
                  field="website"
                  context={{ name }}
                  onAccept={setWebsite}
                  emphasize={!website && Boolean(name.trim())}
                />
              </div>
            </div>
          </div>
          {error && (
            <div className="rounded-md border border-red-800 bg-red-950/40 px-3 py-2 text-sm text-red-300">
              {error}
            </div>
          )}
          <div className="flex justify-end gap-2 pt-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={onClose}
              disabled={submitting}
            >
              {t('common.cancel')}
            </Button>
            <Button type="submit" size="sm" disabled={!canSubmit}>
              {submitting
                ? t('inlineCreateAccount.creating')
                : t('inlineCreateAccount.submit')}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
